import React, {
  MouseEventHandler,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { NextSeo } from "next-seo";
import {
  PROMPT_EDIT_ASPECT_RATIO_LABELS,
  PROMPT_EDIT_ASPECT_RATIOS,
  PROMPT_EDIT_ASPECT_RATIOS_FOR_GPT,
  PROMPT_EDIT_MODEL_OPTIONS,
  PROMPT_EDIT_QUALITIES,
  PROMPT_EDIT_QUALITIES_FOR_FLUX_KONTEXT,
  PROMPT_EDIT_QUALITY_LABELS,
  PromptEditAspectRatio,
  PromptEditQuality,
} from "domains/assets/constants/promptEdit";
import useAssetPromptEdit from "domains/assets/hooks/useAssetPromptEdit";
import { useAssetPromptEditCu } from "domains/assets/hooks/useAssetPromptEditCu";
import FileManagerImage from "domains/file-manager/components/FileManagerImage";
import { FmImageCardActionsProps } from "domains/file-manager/components/FileManagerImage/Card/Actions";
import { FmImageListActionsProps } from "domains/file-manager/components/FileManagerImage/List/Actions";
import CopyPromptButton from "domains/file-manager/components/FileManagerImage/List/CopyPromptButton";
import ReuseButton from "domains/file-manager/components/FileManagerImage/List/ReuseButton";
import { FILTER_IMAGE_TYPES_KEYS_ONLY_GENERATIONS } from "domains/file-manager/constants/AssetFilter";
import { useGetFileImagePlaceholders } from "domains/file-manager/hooks/useGetFileImagePlaceholders";
import { disabledOnWheel } from "domains/forms/disabledOnWheel";
import ButtonSectionTopIcon from "domains/inference/components/InferenceGenerator/Sidebar/ButtonSectionTopIcon";
import FragmentReferenceImages from "domains/inference/components/InferenceGenerator/Sidebar/FragmentReferenceImages";
import SidebarSection from "domains/inference/components/InferenceGenerator/Sidebar/Section";
import SectionPrompt from "domains/inference/components/InferenceGenerator/Sidebar/SectionPrompt";
import SectionReferenceImageBase from "domains/inference/components/InferenceGenerator/Sidebar/SectionReferenceImageBase";
import { FM_HEADER_TOP_0 } from "domains/inference/constants/fileManagerStyle";
import {
  SectionsProvider,
  useSectionsContext,
} from "domains/inference/contexts/SectionsProvider";
import useImportParamsPromptEdit from "domains/inference/hooks/useImportParamsPromptEdit";
import {
  JobSessionProvider,
  useJobSessionContext,
} from "domains/jobs/contexts/JobSessionProviderV2";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { LaunchShortcut } from "domains/shortcuts/components/Shortcut";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { usePlanContext } from "domains/teams/hooks/usePlan";
import ButtonWithCuIndicator from "domains/ui/components/ButtonWithCuIndicator";
import ControlButtonSelect from "domains/ui/components/ControlButtonSelect";
import ControlSelect from "domains/ui/components/ControlSelect";
import Divider from "domains/ui/components/Divider";
import {
  GeneratorContainer,
  GeneratorContent,
  GeneratorSidebar,
} from "domains/ui/components/GeneratorLayout";
import Icon from "domains/ui/components/Icon";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import ScenarioInput from "domains/ui/components/ScenarioInput";
import SidebarTitle from "domains/ui/components/Sidebar/SidebarTitle";
import Slider from "domains/ui/components/Slider";
import Tag from "domains/ui/components/Tag";
import WithLabelAndTooltip from "domains/ui/components/WithLabelAndTooltip";
import { AnalyticsEvents } from "infra/analytics/constants/Events";
import Track from "infra/analytics/Track";
import { useHandleApiError } from "infra/api/error";
import { removeCuFromType } from "infra/api/ExcludeCU";
import {
  GetAssetsByAssetIdApiResponse,
  GetModelsByModelIdApiResponse,
  usePostPromptInferencesMutation,
} from "infra/api/generated/api";
import _ from "lodash";

import { HStack, Spinner, VStack } from "@chakra-ui/react";

export default function ImagePromptEditPage() {
  return (
    <>
      <NextSeo title="Edit with Prompts" />

      <SectionsProvider defaultCollapsedSections={["refImages"]}>
        <JobSessionProvider
          forcedFilterTypeOptions={[
            ...FILTER_IMAGE_TYPES_KEYS_ONLY_GENERATIONS,
          ]}
        >
          <ImagePromptEdit />
        </JobSessionProvider>
      </SectionsProvider>
    </>
  );
}

// ------------------------------------

export function ImagePromptEdit() {
  const { openSection } = useSectionsContext();
  const { infoToast } = useScenarioToast();
  const handleApiError = useHandleApiError();
  const { files, jobs, isLoading, fileManagerProps, addJob } =
    useJobSessionContext();
  const { maxBatchPremium, showLimitModal } = usePlanContext();
  const { selectedProject } = useTeamContext();
  const [generatePromptTrigger] = usePostPromptInferencesMutation();
  const {
    form,
    setForm,
    setValue,
    setReference,
    handlePromptEditJob,
    isPromptEditLoading,
  } = useAssetPromptEdit();
  const [propositions, setPropositions] = useState<string[]>([]);
  const [isMagicPromptLoading, setIsMagicPromptLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { cuCost, isCuLoading } = useAssetPromptEditCu({
    assetId: form.reference?.id,
    form,
  });

  const isSubmitButtonDisabled = !(form.reference && form.prompt);
  const isSuggestButtonDisabled = !form.reference;

  const placeholders = useGetFileImagePlaceholders({
    length: 1,
    ...(form.reference?.metadata.width && form.reference?.metadata.height
      ? {
          dimensions: {
            width: form.reference.metadata.width,
            height: form.reference.metadata.height,
          },
        }
      : {}),
  });

  const allImages = useMemo(() => {
    if (form.reference) {
      return [form.reference, ...form.refImages];
    }
    return [];
  }, [form.reference, form.refImages]);

  // ----------------------------------

  const handleCopyPromptClick = useCallback<
    ListActionsProps["onCopyPromptClick"]
  >(
    ({ prompt }) => {
      openSection("prompt");
      setValue("prompt", prompt);

      infoToast({
        title: "Settings updated",
      });
    },
    [openSection, setValue, infoToast]
  );

  const handleRefineClick = useCallback<
    CardActionsCustomProps["onRefineClick"]
  >(
    async (asset) => {
      await setReference(asset);

      infoToast({
        title: "Settings updated",
      });
    },
    [setReference, infoToast]
  );

  const handleSuggestionReloadClick = useCallback(async () => {
    if (!allImages.length) {
      return;
    }

    try {
      setIsMagicPromptLoading(true);
      openSection("suggestions");

      const { prompts } = await generatePromptTrigger({
        projectId: selectedProject.id,
        body: {
          mode: "image-editing",
          modelId: form.modelId,
          image: allImages.length === 1 ? allImages[0].id : undefined,
          images:
            allImages.length > 1
              ? allImages.map((image) => image.id)
              : undefined,
          prompt: "",
          numResults: 5,
        },
      })
        .unwrap()
        .then(removeCuFromType);

      setPropositions(prompts);
    } catch (error: any) {
      handleApiError(
        error,
        "There was an error generating the prompt. Please try again.",
        {
          quota: () => {
            if (_.get(error, "data.details.remainingSeconds")) {
              showLimitModal("planCooldown", {
                timeout: error.data.details.remainingSeconds,
                type: "magicPrompt",
              });
            } else {
              showLimitModal("planMagicPromptLimit");
            }
          },
        }
      );
    } finally {
      setIsMagicPromptLoading(false);
    }
  }, [
    form.modelId,
    allImages,
    generatePromptTrigger,
    handleApiError,
    openSection,
    selectedProject.id,
    showLimitModal,
  ]);

  // ----------------------------------

  const editImage = useCallback(async () => {
    if (isSubmitButtonDisabled) {
      return;
    }
    const job = await handlePromptEditJob({
      assetId: form.reference!.id,
      trackingExtraParams: {
        origin: "prompt edit page",
      },
    });
    if (job) addJob(job);
  }, [form.reference, handlePromptEditJob, addJob, isSubmitButtonDisabled]);

  // ----------------------------------

  const listActionsProps = useMemo(
    () => ({
      onCopyPromptClick: handleCopyPromptClick,
    }),
    [handleCopyPromptClick]
  );

  const cardActionsProps = useMemo(
    () => ({
      onRefineClick: handleRefineClick,
    }),
    [handleRefineClick]
  );

  // ----------------------------------

  useImportParamsPromptEdit({
    setForm,
    setReference,
    onValueChange: openSection,
  });

  // ----------------------------------

  return (
    <GeneratorContainer onSubmit={editImage}>
      <GeneratorSidebar>
        <SidebarTitle
          title="Edit with Prompts"
          videoUrl="https://www.youtube.com/embed/eJL_hKSJXzM?si=WEo_iptEUVawbxeJ"
          videoTooltip="Watch a quick video to learn about modifying images using simple text instructions"
        />
        <Divider />

        <SidebarSection>
          <ControlButtonSelect
            title="Model"
            options={PROMPT_EDIT_MODEL_OPTIONS}
            selectedOption={form.modelId}
            onOptionSelected={(newValue) => setValue("modelId", newValue)}
          />
        </SidebarSection>
        <Divider />

        <SectionReferenceImageBase
          id="referenceImage"
          title="Reference"
          asset={form.reference}
          setAsset={setReference}
          withRemoveBackground={
            form.reference?.metadata.width && form.reference?.metadata.height
              ? form.reference.metadata.width *
                  form.reference.metadata.height <=
                50_000_000
              : false
          }
          removeBackgroundTrackingExtraParams={{
            origin: "prompt edit page",
          }}
          withSketch="edit"
          selectLibrary={{
            tabs: ["image"],
          }}
        />
        <Divider />

        <SectionPrompt
          id="prompt"
          title="Instructions"
          rewriteMode="image-editing"
          prompt={form.prompt}
          setPrompt={(newValue) => setValue("prompt", newValue)}
          withAssetUpload={false}
          placeholder="Provide detailed instructions for the AI model to edit your image."
          withSuggest
          onSuggestClick={
            !isSuggestButtonDisabled ? handleSuggestionReloadClick : undefined
          }
          suggestTooltip="Generate suggestions"
          promptSparkImages={allImages}
          getModel={async () =>
            ({
              id: form.modelId,
            } as GetModelsByModelIdApiResponse["model"])
          }
        />
        <Divider />

        {(!!propositions.length || isMagicPromptLoading) && (
          <>
            <SidebarSection
              headerProps={{
                id: "suggestions",
                title: (
                  <HStack>
                    <span>Suggestions</span>
                    {!!propositions.length && isMagicPromptLoading && (
                      <Spinner size="xs" />
                    )}
                  </HStack>
                ),
                topRight: propositions.length ? (
                  <ButtonSectionTopIcon
                    tooltip="Clean"
                    tooltipProps={{ placement: "right" }}
                    onClick={() => setPropositions([])}
                  >
                    <Icon id="Ui/TrashMd" h="14px" />
                  </ButtonSectionTopIcon>
                ) : undefined,
              }}
            >
              {!propositions.length && isMagicPromptLoading && (
                <Spinner size="xs" />
              )}

              {!!propositions.length && (
                <HStack flexWrap="wrap" spacing={2}>
                  {propositions.map((proposition, idx) => (
                    <Tag
                      key={`${idx}-${proposition}`}
                      canEdit={false}
                      label={proposition}
                      onClick={() => setValue("prompt", proposition)}
                      isTruncated
                    />
                  ))}
                </HStack>
              )}
            </SidebarSection>
            <Divider />
          </>
        )}

        <SidebarSection>
          <ButtonWithCuIndicator
            cuCost={cuCost}
            isCuLoading={isCuLoading}
            onClick={editImage}
            isLoading={isPromptEditLoading}
            isDisabled={isSubmitButtonDisabled}
            w="100%"
            tooltip={<LaunchShortcut />}
            tooltipProps={{
              placement: "right",
              hasArrow: true,
              isDisabled: isSubmitButtonDisabled,
            }}
          >
            Generate
          </ButtonWithCuIndicator>
        </SidebarSection>
        <Divider />

        <SidebarSection
          headerProps={{
            title: "Additional Images",
            id: "refImages",
            tooltip:
              form.modelId === "flux-kontext"
                ? "Upload up to 3 extra images for the AI."
                : "Upload up to 5 extra images for the AI.",
            topRight:
              form.refImages.length > 0 ? (
                <ButtonSectionTopIcon
                  tooltip="Remove All"
                  tooltipProps={{ placement: "right" }}
                  onClick={() => setValue("refImages", [])}
                >
                  <Icon id="Ui/TrashMd" h="14px" />
                </ButtonSectionTopIcon>
              ) : undefined,
          }}
        >
          <FragmentReferenceImages
            assets={form.refImages}
            setAssets={(newValue) => setValue("refImages", newValue)}
            maxImages={form.modelId === "flux-kontext" ? 3 : 5}
            removeBackgroundTrackingExtraParams={{
              origin: "prompt edit page",
            }}
            selectLibrary={{
              tabs: ["image"],
            }}
            kind="image"
          />
        </SidebarSection>
        <Divider />

        <SidebarSection
          headerProps={{
            title: "Settings",
            id: "settings",
          }}
        >
          <VStack align="stretch">
            <WithLabelAndTooltip
              label="Image count"
              premiumTooltip={
                maxBatchPremium < 4
                  ? "Upgrade to generate larger batches (up to 4 images)"
                  : undefined
              }
            >
              <Slider
                withNumberInput
                max={maxBatchPremium}
                min={1}
                onChange={(value) => setValue("count", value)}
                step={1}
                value={form.count}
              />
            </WithLabelAndTooltip>

            {["gpt-image-1", "flux-kontext"].includes(form.modelId) && (
              <>
                <ControlSelect
                  title="Aspect Ratio"
                  value={form.aspectRatio}
                  setValue={(newValue) => setValue("aspectRatio", newValue)}
                  options={(form.modelId === "flux-kontext"
                    ? PROMPT_EDIT_ASPECT_RATIOS
                    : PROMPT_EDIT_ASPECT_RATIOS_FOR_GPT
                  ).map((aspectRatio: PromptEditAspectRatio) => ({
                    label: PROMPT_EDIT_ASPECT_RATIO_LABELS[aspectRatio],
                    value: aspectRatio,
                  }))}
                />

                <ControlSelect
                  title="Quality"
                  value={form.quality}
                  setValue={(newValue) => setValue("quality", newValue)}
                  options={(form.modelId === "flux-kontext"
                    ? PROMPT_EDIT_QUALITIES_FOR_FLUX_KONTEXT
                    : PROMPT_EDIT_QUALITIES
                  ).map((quality: PromptEditQuality) => ({
                    label: PROMPT_EDIT_QUALITY_LABELS[quality],
                    value: quality,
                  }))}
                />
              </>
            )}

            {form.modelId === "flux-kontext" && (
              <>
                <WithLabelAndTooltip label="Guidance">
                  <Slider
                    withNumberInput
                    max={20}
                    min={1}
                    onChange={(value) => setValue("guidanceScale", value)}
                    step={0.1}
                    value={form.guidanceScale}
                  />
                </WithLabelAndTooltip>
                <WithLabelAndTooltip
                  label="Seed"
                  tooltip="Use a custom seed for targeted results (single-image inference)"
                >
                  <ScenarioInput
                    value={form.seed ?? ""}
                    setValue={(newValue) => setValue("seed", newValue)}
                    placeholder="Random"
                    type="number"
                    onWheel={disabledOnWheel}
                  />
                </WithLabelAndTooltip>
              </>
            )}
          </VStack>
        </SidebarSection>
      </GeneratorSidebar>

      <GeneratorContent scrollRef={scrollRef}>
        <FileManagerImage
          scrollRef={scrollRef}
          files={
            !files?.length && !jobs?.length && !isLoading ? placeholders : files
          }
          jobs={jobs}
          isSelectable
          isLoading={isLoading}
          ListActionsComponent={ListActions}
          listActionsProps={listActionsProps}
          CardActionsComponent={CardActions}
          cardActionsProps={cardActionsProps}
          {...fileManagerProps}
          styleProps={FM_HEADER_TOP_0}
        />
      </GeneratorContent>
    </GeneratorContainer>
  );
}

// ------------------------------------

export type CardActionsCustomProps = {
  onRefineClick: (asset: GetAssetsByAssetIdApiResponse["asset"]) => void;
};

export type CardActionsProps = FmImageCardActionsProps<CardActionsCustomProps>;

function CardActions({
  asset,
  top,
  bottom,
  onRefineClick,
  onActionClick,
}: CardActionsProps) {
  const assetId = asset?.id;
  const canReuseImage = !["vectorization"].includes(asset?.metadata.type ?? "");
  const hasPrompt =
    asset?.metadata.type === "image-prompt-editing" && !!asset?.metadata.prompt;

  const handleImageClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      Track(AnalyticsEvents.CardAction.ReusedImage);
      if (asset) {
        onRefineClick(asset);
      }
    },
    [onRefineClick, asset]
  );

  if (!canReuseImage) {
    return null;
  }

  return (
    <>
      {top}

      {canReuseImage && (
        <MenuItem
          w="full"
          iconId="Nav/Image/Outline"
          iconH="12px"
          onClick={handleImageClick}
          text="Image"
        />
      )}

      {hasPrompt && (
        <MenuItem
          w="full"
          iconId="Ui/Prompt"
          iconH="12px"
          internalLink={{
            pathname: "/edit-with-prompts",
            query: {
              importPromptFromAssetId: assetId,
            },
          }}
          onClick={() => onActionClick?.("usePrompt")}
          text="Prompt"
        />
      )}

      {bottom}
    </>
  );
}

// ------------------------------------

export type ListActionsCustomProps = {
  onCopyPromptClick: (arg: { prompt: string }) => void;
};

export type ListActionsProps = FmImageListActionsProps<ListActionsCustomProps>;

function ListActions({ job, firstAsset, onCopyPromptClick }: ListActionsProps) {
  const { isReader } = useTeamContext();
  const isActionDisplayed =
    ["image-prompt-editing"].includes(job?.jobType ?? "") && !isReader;

  if (isReader) {
    return null;
  }

  return (
    <>
      {isActionDisplayed && <ReuseButton job={job} firstAsset={firstAsset} />}
      <CopyPromptButton
        job={job}
        firstAsset={firstAsset}
        onClick={onCopyPromptClick}
      />
    </>
  );
}
